import React, { useState, useRef, useEffect } from 'react';
import {
    Breadcrumb,
    Layout,
    Input,
    Button,
    Spin,
    Typography,
    Table,
    Tag,
    message,
    Card,
    Row, // Added Row
    Col,
    Space, // Added Col
} from 'antd';
import axios from 'axios';
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings';
import Headerdiv from '../layout/header';
import Footerdiv from '../layout/footer';
import { AlertError, AlertSuccess } from '../usableclasses';
import { Link } from 'react-router-dom'; // Import Link for quick links

const { Content } = Layout;
const { Text } = Typography;

const orderRefRegex = /^(\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])([0-9]{4})$/;

const MarkAsReady = () => {
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");
    const UPDATED_BY = localStorage.getItem("USERID");

    const [loading, setLoading] = useState(false);
    const [orderRef, setOrderRef] = useState('');
    const [orders, setOrders] = useState([]);

    const inputRef = useRef();

    useEffect(() => {
        if (!loading) {
            inputRef.current?.focus();
        }
    }, [loading]);

    const addOrderResult = (ref, status) => {
        setOrders(prev => [
            ...prev,
            {
                key: prev.length,
                orderNo: ref,
                status,
            },
        ]);
    };

    const processScannedOrder = async (ref) => {
        if (loading) return;
        setLoading(true);

        try {
            const response = await axios.post(
                `${ApiURL}/warehouse/scantoprocessdone`,
                {
                    orderRef: ref,
                    completedBy: UPDATED_BY,
                },
                {
                    headers: {
                        SystemAccessToken: TOKEN,
                    },
                }
            );

            const success = response.data;
            const status = success ? 'SUCCESS' : (response.data?.message || 'FAILED');
            addOrderResult(ref, status);
            AlertSuccess(`Order ${ref} marked as ready successfully.`);
        } catch (error) {
            const errMsg = error?.response?.data?.message || 'Failed to update order status.';
            AlertError(errMsg);
            addOrderResult(ref, `FAILED: ${errMsg}`);
        } finally {
            setLoading(false);
            setOrderRef('');
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setOrderRef(value);

        const trimmed = value.trim();
        if (orderRefRegex.test(trimmed)) {
            processScannedOrder(trimmed);
        }
    };

    const handleManualSubmit = () => {
        if (loading) return;

        const trimmed = orderRef.trim();
        if (!trimmed) {
            AlertError('Please enter or scan an order reference.');
            return;
        }

        if (!orderRefRegex.test(trimmed)) {
            message.error('Invalid Order Reference format.');
            addOrderResult(trimmed, 'INVALID_FORMAT');
            setOrderRef('');
            return;
        }

        processScannedOrder(trimmed);
    };

    const columns = [
        {
            title: 'ORDER NO',
            dataIndex: 'orderNo',
            key: 'orderNo',
        },
        {
            title: 'STATUS',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'red';
                if (status === 'UPDATED' || status?.toUpperCase().startsWith('SUCCESS')) {
                    color = 'green';
                } else if (status === 'INVALID_FORMAT') {
                    color = 'orange';
                }
                return <Tag color={color}>{status}</Tag>;
            },
        },
    ];

    return (
        <Spin spinning={loading} tip="Processing...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: '100vh' }}>
                <Headerdiv />
                <Content style={{ padding: '20px' }}>
                    <Breadcrumb style={{ marginBottom: '16px' }}>
                        <Breadcrumb.Item>Warehouse</Breadcrumb.Item>
                        <Breadcrumb.Item>Mark As Ready</Breadcrumb.Item>
                    </Breadcrumb>

                    <Card style={{ marginBottom: '20px' }}>
                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} sm={16} md={18}>
                                <Input
                                    ref={inputRef}
                                    placeholder="Scan or type order reference"
                                    value={orderRef}
                                    onChange={handleInputChange}
                                    onPressEnter={handleManualSubmit}
                                    size="large" // Make input larger for better UX
                                    style={{ borderRadius: '8px' }}
                                />
                            </Col>
                            <Col xs={24} sm={8} md={6}>
                                <Button
                                    type="primary"
                                    onClick={handleManualSubmit}
                                    size="large" // Make button larger
                                    block // Make button take full width
                                >
                                    Mark As Ready
                                </Button>
                            </Col>
                        </Row>
                    </Card>

                    <Card title="Scan History" style={{ marginBottom: '20px' }}>
                        <Table
                            columns={columns}
                            dataSource={orders}
                            pagination={false}
                            bordered
                            rowKey="key"
                            scroll={{ x: 'max-content' }} // Make table horizontally scrollable
                            locale={{
                                emptyText: 'No orders scanned yet.'
                            }}
                        />
                    </Card>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};
export default MarkAsReady;
